import { PortalError } from "@/types/portal-error";
import { Service } from "../mainService";
import { UsuarioSumario } from "@/types/usuario-sumario";
import { FiltrosUsuarios } from "@/types/administracao/usuarios/filtros-usuarios";
import { Usuario } from "@/types/administracao/usuarios/usuario";
import { UsuarioResumido } from "@/types/administracao/usuarios/usuario-resumido";
import { EnvioUsuarioPermissoes } from "@/types/administracao/usuarios/envio-usuario-permissoes";
import { UsuarioPerfil } from "@/types/administracao/usuarios/usuario-perfil";
import { ResponseUsuario } from "@/types/administracao/usuarios/response-usuario";
import { MudancaSenha } from "@/types/administracao/mudanca-senha";
import { NovoUsuario } from "@/types/administracao/usuarios/novo-usuario";
import { LoginData } from "@/types/administracao/usuarios/login-data";
const usuarioService = {
  adicionarPermissaoUsuario,
  alterarComunicacao,
  atualizarDadosUsr,
  atualizarNovoUsuario,
  alterarEmail,
  aprovarNovoUsuario,
  cancelarSolicitacaoNovoUsuario,
  confirmarCodigoAtivacao,
  criarNovoUsuario,
  findByCpfNome,
  filtrarUsuarios,
  getNovoUsuarioById,
  getUsuarioById,
  listarNovosUsuarios,
  login,
  loginCertStart,
  loginCertFinish,
  loginInterno,
  loginUg,
  meuPerfil,
  obterAnexoDeNovoUsuario,
  resetaSenha,
  reativarUsuario,
  removePerfil,
  removerPerfilPermissao,
  removerPermissaoUsuario,
  solicitarMudancas,
  trocarEmail,
  trocarSenha,
  trocarSenhaNovoUsuario,
  usuariosDaUg,
  verificaCpf,
  verificaCpfNovoUsuario,
  reenviarAtivacao,
  updateComCertificado,
  enviarEmailTrocaSenha,
  validarCodigoTrocaSenha,
  trocarSenhaEsquecida,
  getUsuariosByCpfs,
};

async function findByCpfNome(
  filtro: string,
  ativo?: boolean
): Promise<UsuarioSumario[]> {
  const ativoString =
    ativo === undefined ? "" : ativo ? "&ativo=true" : "&ativo=false";
  const url = `${process.env.VUE_APP_USERS_API}/usuarios/findByCpfNome?cpfNome=${filtro}${ativoString}`;
  const response = await Service.$http.get(url);
  if (!response || response.status !== 200) {
    console.error("Falha ao buscar usuários", response);
    throw new PortalError(response.data?.msg ?? "Falha ao buscar usuários");
  }
  return response.data.map((u) => new UsuarioSumario(u));
}

async function filtrarUsuarios(
  filtro: FiltrosUsuarios,
  currentPage: number
): Promise<ResponseUsuario> {
  const url = `${process.env.VUE_APP_API_USUARIOS
    }usuario?${filtro.toQueryString()}&page=${currentPage}`;
  const response = await Service.$http.get(url);
  if (response.status === 200) return new ResponseUsuario(response.data);
  console.error("Erro ao buscar usuários", response);
  throw new PortalError("Erro ao buscar usuários: " + response);
}

async function getUsuarioById(idUsuario: string): Promise<Usuario> {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_API_USUARIOS}usuario/${idUsuario}`
  );
  if (response.status === 200) return new Usuario(response.data);
  console.error("Erro ao buscar usuário por id", response);
  throw new PortalError("Erro ao buscar usuário");
}

/**
 * Reset de senha de um usuário.
 *
 * @since 20240919
 */
async function resetaSenha(idUsuario: string): Promise<boolean> {
  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_USUARIOS}usuario/resetPassword/${idUsuario}`
  );
  if ((response && response.status === 200) || response.status === 201) {
    return response;
  }
  console.error(response);
  throw new PortalError("Falha ao tentar resetar a senha.");
}

/**
 * Reativar usuário.
 *
 * @since 20240919
 */
async function reativarUsuario(idUsuario: string): Promise<boolean> {
  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_USUARIOS}usuario/reativarUsuario/${idUsuario}`
  );
  if ((response && response.status === 200) || response.status === 201) {
    return response;
  }
  console.error(response);
  throw new PortalError("Falha ao reativar usuário.");
}

/**
 * Remoção de perfil de um usuário.
 *
 * @since 20240919
 */
async function removePerfil(idUsuario: string): Promise<boolean> {
  const url = `${process.env.VUE_APP_API_USUARIOS}usuario-perfil/perfisUsuario/${idUsuario}`;
  const response = await Service.$http.delete(url);
  if (response) return response.status === 200;
  console.error(response);
  throw new PortalError("Falha ao tentar remover perfil.");
}

/**
 * Salva usuário novo.
 *
 * @since 20241023
 */
async function criarNovoUsuario(usr, atualiza?: boolean): Promise<Usuario> {
  try {
    if (usr?.ID_CRP_SEXO) usr.ID_CRP_SEXO = parseInt(usr?.ID_CRP_SEXO);
    if (typeof usr?.DATA_INICIO !== "string")
      usr.DATA_INICIO = usr?.DATA_INICIO.toISOString();
    const response = await Service.$http.post(
      `${process.env.VUE_APP_API_USUARIOS}usuario?atualizaNovoUsuario=${atualiza ? "true" : "false"
      }`,
      usr
    );
    if ((response && response.status === 200) || response.status === 201) {
      const novoUsuario: Usuario = response.data;
      return novoUsuario;
    }
    throw new PortalError("Falha ao salvar dados do usuário.");
  } catch (e: any) {
    console.error("capotou no service", e);
    if ([400, 403].includes(e.response?.status)) throw montarArrayDeErros(e);
    throw e;
  }
}

/**
 * Salva dados editados do usuário.
 *
 * @since 20240926
 */
async function atualizarDadosUsr(
  usr,
  idUsuario: number | undefined
): Promise<Usuario> {
  try {
    //Remove os perfis do usuario para evitar problema de sobrecarga
    delete usr.perfis;

    if (usr?.ID_CRP_SEXO) usr.ID_CRP_SEXO = parseInt(usr?.ID_CRP_SEXO);
    if (typeof usr?.DATA_INICIO !== "string")
      usr.DATA_INICIO = usr?.DATA_INICIO.toISOString();
    const response = await Service.$http.patch(
      `${process.env.VUE_APP_API_USUARIOS}usuario/${idUsuario}`,
      usr
    );
    if (response.status === 200)
      return Object.assign(new Usuario(), response.data.usuario);
    throw new PortalError("Falha ao salvar dados do usuário.");
  } catch (e: any) {
    console.error("capotou no service", e);
    if ([400, 403].includes(e.response?.status)) throw montarArrayDeErros(e);
    throw e;
  }
}

function montarArrayDeErros(e: any): PortalError {
  if (e.response.data.erros) return new PortalError(e.response.data.erros);
  if (e.response.data.message) return new PortalError(e.response.data.message);
  return new PortalError(e);
}

//
//métodos privados
//
function generateFictionalUsuarios(): UsuarioResumido[] {
  const usuarios: UsuarioResumido[] = [];
  for (let i = 1; i <= 10; i++) {
    const usuario = new UsuarioResumido({
      IdUsuario: i,
      Nome: `Usuario ${i}`,
      Cpf: `000.000.000-${i.toString().padStart(2, "0")}`,
      Inativo: i % 2 === 0 ? "Sim" : "Não",
      Email: `usuario${i}@example.com`,
    });
    usuarios.push(usuario);
  }
  return usuarios;
}

/**
 * Adicionar novas permissoes ao usuario (UGs e Perfis de acesso)
 *
 * @since 20241022
 */
async function adicionarPermissaoUsuario(
  data: EnvioUsuarioPermissoes[]
): Promise<UsuarioPerfil[]> {
  const url = `${process.env.VUE_APP_API_USUARIOS}usuario-perfil`;
  const response = await Service.$http.post(url, data);
  if (response && response.status === 201)
    return response.data.map((up) => new UsuarioPerfil(up));
  throw new PortalError("Falha ao tentar adicionar permissão.");
}

/**
 * Excluir um perfil específico
 *
 * @since 20241022
 */
async function removerPerfilPermissao(id: number) {
  const url = `${process.env.VUE_APP_API_USUARIOS}usuario-perfil/${id}`;
  const response = await Service.$http.delete(url);
  if (response && response.status === 200) return response;
  throw new PortalError("Falha ao tentar remover perfil da permissão.");
}

/**
 * Excluir uma permissão do usuário (UG com todos os perfis)
 *
 * @since 20241022
 */
async function removerPermissaoUsuario(idUsuario: string, idUg: string) {
  const url = `${process.env.VUE_APP_API_USUARIOS}usuario-perfil/perfisUsuarioUg/${idUsuario}/${idUg}`;
  const response = await Service.$http.delete(url);
  if (response && response.status === 200) return response;
  throw new PortalError("Falha ao tentar remover permissão.");
}

/**
 * Get dos usuários da UG selecionada no portal.
 *
 * @since 20241016
 */
async function usuariosDaUg(): Promise<UsuarioResumido[]> {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_API_USUARIOS}usuario/ug`
  );
  if (response.status === 200)
    return response.data.map((upg: any) => new UsuarioResumido(upg));
  console.error("Erro ao buscar usuários da ug", response);
  throw new PortalError("Erro ao buscar usuários");
}

/**
 * Informações do usuário logado.
 *
 * @since 20241030
 */
async function meuPerfil(): Promise<Usuario> {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_API_USUARIOS}usuario/meuPerfil`
  );
  if ((response && response.status === 200) || response.status === 201)
    return new Usuario(response.data);
  console.error("Erro ao buscar info de usuário", response);
  throw new PortalError("Erro ao buscar usuário");
}

/**
 * Trocar senha de login.
 *
 * @since 20241031
 */
async function trocarSenha(senhasObj: MudancaSenha): Promise<boolean> {
  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_USUARIOS}usuario/trocarSenha`,
    senhasObj
  );
  return (response && response.status === 200) || response.status === 201;
}

/**
 * Troca email de login
 *
 * @since 20241112
 */
async function trocarEmail(data: { EMAIL: string }) {
  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_USUARIOS}usuario/atualizarEmail`,
    data
  );
  if (response && response.status === 200) return response;
  else {
    throw new PortalError("Erro ao alterar email do usuário.");
  }
}

/**
 * Altera preferência de comunicação do usuário
 *
 *  @since 20241112
 */
async function alterarComunicacao(preferencia: boolean) {
  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_USUARIOS}usuario/atualizarComunicaTelefone/${preferencia}`
  );
  if (response && response.status === 200) return response;
  else throw new PortalError("Erro ao alterar preferência de comunicação.");
}

async function verificaCpf(cpf: string): Promise<null | number> {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_API_USUARIOS}usuario/verificaCpf/${cpf}`
  );
  if (response && response.status === 200) return response.data.ID_USUARIO;
  else throw new PortalError("Erro ao verificar CPF");
}

/**
 *Lista todos os novos usuários a serem aprovados
 *
 *  @since 20241125
 */
async function listarNovosUsuarios(
  page: number,
  nome?: string,
  cpf?: string,
  situacao?: string,
  email?: string
): Promise<{ items: NovoUsuario[]; total: number }> {
  let url = `${process.env.VUE_APP_API_USUARIOS}novo-usuario?page=${page}&perPage=10`;

  // Adicionar parâmetros opcionais à URL
  if (nome && nome !== "") url += `&nome=${encodeURIComponent(nome)}`;
  if (cpf && cpf !== "") url += `&cpf=${encodeURIComponent(cpf)}`;
  if (situacao && situacao !== "")
    url += `&situacao=${encodeURIComponent(situacao)}`;
  if (email && email !== "") url += `&email=${encodeURIComponent(email)}`;

  const response = await Service.$http.get(url);
  if (response && response.status === 200) {
    const listaNovosUsuarios = response.data.data.map(
      (nu: any) => new NovoUsuario(nu)
    );
    return {
      items: listaNovosUsuarios,
      total: response.data.meta.total,
    };
  } else throw new PortalError("Erro ao listar novos usuários.");
}

/**
 *Exibe um novo usuário chamado por ID
 *
 *  @since 20241126
 */
async function getNovoUsuarioById(id: string): Promise<NovoUsuario> {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_API_USUARIOS}novo-usuario/${id}`
  );
  if (response && response.status === 200)
    return new NovoUsuario(response.data);
  else throw new PortalError("Erro ao listar novos usuários");
}

/**
 * Edita informações do novo usuário pela tela do admnistrador
 *
 * @since 20241129
 */
async function atualizarNovoUsuario(id: string, data: any) {
  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_USUARIOS}novo-usuario/admin/atualizar/${id}`,
    data
  );

  if (response && response.status === 200) return response;
  else throw new PortalError("Erro ao atualizar informações do novo usuário");
}

/**
 * Alterar email do novo usuário pela tela do admnistrador
 *
 * @since 20260505
 */
async function alterarEmail(id: string, data: any) {
  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_USUARIOS}novo-usuario/admin/alterarEmail/${id}`,
    data
  );

  if (response && response.status === 200) return response;
  else throw new PortalError("Erro ao atualizar informações do novo usuário");
}

/**
 * Aprova a solicitação do novo usuário
 *
 * @since 20241129
 */
async function aprovarNovoUsuario(id: string) {
  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_USUARIOS}novo-usuario/admin/aprovar/${id}`
  );
  if (response && response.status === 200) return response;
  else throw new PortalError("Erro ao aprovar o novo usuário.");
}

/**
 * Solicita a troca de senha do usuario
 *
 * @since 20241129
 */
async function trocarSenhaNovoUsuario(id: string) {
  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_USUARIOS}novo-usuario/admin/resetarSenha/${id}`
  );
  if (response && response.status === 200) return response;
  else throw new PortalError("Errro ao trocar senha do usuário");
}

/**
 * Solicita alterações e envia um email ao usuário
 *
 * @since 20241129
 */
async function solicitarMudancas(id: string, data: string[]) {
  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_USUARIOS}novo-usuario/admin/solicitarDados/${id}`,
    data
  );

  if (response && response.status === 200) return response;
  else throw new PortalError("Erro ao solicitar alterações para o usuário");
}

/**
 * Cancela a solicitação do novo usuário
 *
 * @since 20241202
 */
async function cancelarSolicitacaoNovoUsuario(id: string): Promise<any> {
  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_USUARIOS}novo-usuario/admin/cancelar/${id}`
  );
  if (response && response.status === 200) return response;
  else throw new PortalError("Errro ao cancelar solicitação do usuário");
}

async function confirmarCodigoAtivacao(codigo: string): Promise<string> {
  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_USUARIOS}usuario/confirmarEmail/${codigo}`
  );
  if (response && response.status === 200) return response.data?.msg;
  else throw new PortalError("Erro ao confirmar código de ativação");
}


async function verificaCpfNovoUsuario(cpf: string): Promise<null | number> {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_API_USUARIOS}novo-usuario/admin/verificaCpf/${cpf}`
  );
  if (response && response.status === 200) return response.data.ID_NOVO_USUARIO;
  else throw new PortalError("Erro ao verificar CPF");
}

async function login(usuario: string, senha: string): Promise<LoginData> {
  const url = `${process.env.VUE_APP_API_USUARIOS}auth/login`;
  const data = {
    username: usuario,
    password: senha,
  };
  const response = await Service.$http.post(url, data);
  if (!response || response.status !== 201) {
    console.error("Falha ao realizar login", response);
    throw new PortalError(response.data?.msg ?? "Falha ao realizar login");
  }
  return new LoginData(response.data);
}

async function loginCertStart(): Promise<string> {
  const url = `${process.env.VUE_APP_API_USUARIOS}auth/startLoginWithCertificate`;
  const response = await Service.$http.get(url);
  if (response?.status === 200) {
    return response.data.token;
  }
  console.error("Falha ao iniciar login com certificado", response);
  throw new PortalError(
    response?.data?.msg ?? "Falha ao iniciar login com certificado"
  );
}

async function loginCertFinish(token: string): Promise<LoginData> {
  const url = `${process.env.VUE_APP_API_USUARIOS}auth/loginWithCertificate/${token}`;
  const response = await Service.$http.post(url);
  if (response?.status === 201) {
    return new LoginData(response.data);
  }
  console.error("Falha ao finalizar login com certificado", token, response);
  throw new PortalError(
    response?.data?.msg ?? "Falha ao finalizar login com certificado"
  );
}

async function loginInterno(usuario: string, senha: string): Promise<LoginData> {
  const url = `${process.env.VUE_APP_API_USUARIOS}auth/loginInterno`;
  const data = {
    username: usuario,
    password: senha,
  };
  const response = await Service.$http.post(url, data);
  if (!response || response.status !== 201) {
    console.error("Falha ao realizar login", response);
    throw new PortalError(response.data?.msg ?? "Falha ao realizar login");
  }
  return new LoginData(response.data);
}

async function loginUg(codigoUg: string): Promise<LoginData> {
  const url = `${process.env.VUE_APP_API_USUARIOS}auth/loginUg`;
  const response = await Service.$http.post(url, { codigoUg });
  if (!response || response.status !== 201) {
    console.error("Falha ao entrar na UG", response);
    throw new PortalError(response?.data?.msg ?? "Falha ao entrar na UG");
  }
  return new LoginData(response.data);
}

async function obterAnexoDeNovoUsuario(idAnexo: number): Promise<any> {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_API_USUARIOS}novo-usuario/admin/downloadAnexo/${idAnexo}`,
      { responseType: "arraybuffer" } // essa jeansgraça como byte
    );
    const contentType = response.headers["content-type"];

    // octet-stream por precaução
    if (
      (response.status === 200 &&
        contentType &&
        contentType.includes("application/pdf")) ||
      contentType.includes("image") ||
      contentType.includes("octet-stream")
    )
      return new Blob([response.data], { type: contentType });
    throw new PortalError("Erro ao obter anexo do novo usuário");
  } catch (e: any) {
    if ([400, 401, 403].includes(e.response?.status)) {
      const decoded = new TextDecoder().decode(e.response.data);
      const json = JSON.parse(decoded);
      e.response.data = json;
      throw montarArrayDeErros(e);
    }
    throw e;
  }
}

async function reenviarAtivacao(tipo = "email"): Promise<any> {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_API_USUARIOS}usuario/reenviarAtivacao/${tipo}`
  );
  if (response && response.status === 200) return response.data;
  else throw new PortalError("Erro ao reenviar codigo de ativação");
}

async function updateComCertificado(nu: NovoUsuario): Promise<LoginData> {
  const form = nu.toUsuarioCertificado();
  const url = `${process.env.VUE_APP_API_USUARIOS}usuario/updateComCertificado`;
  const response = await Service.$http.post(url, form);
  if (response && response.status === 201) {
    return new LoginData(response.data);
  }
  console.error("Falha ao salvar dados do usuário", nu, response);
  throw new PortalError(
    response.data.message ?? "Falha ao salvar dados do usuário"
  );
}
// Troca de senha
async function enviarEmailTrocaSenha(cpf: string): Promise<any> {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_API_USUARIOS}usuario/esqueceuSenha/solicitarCodigo/${cpf}`
  );
  if (response && response.status === 200) return response.data;
  else throw new PortalError("Erro ao reenviar codigo de ativação");
}

async function validarCodigoTrocaSenha(codigo: string): Promise<any> {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_API_USUARIOS}usuario/esqueceuSenha/validarCodigo/${codigo}`
  );
  if (response && response.status === 200) return response.data;
  else throw new PortalError("Erro ao reenviar codigo de ativação");
}

async function trocarSenhaEsquecida(codigo: string, senha: string): Promise<any> {

  const data = {
    senhaNova: senha
  }

  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_USUARIOS}usuario/esqueceuSenha/atualizarSenha/${codigo}`, data
  );
  if (response && response.status === 200) return response.data;
  else throw new PortalError("Erro ao reenviar codigo de ativação");
}

async function getUsuariosByCpfs(cpfs: string[]): Promise<UsuarioSumario[]> {
  const cpfsQuery = cpfs.map((cpf) => `&cpf=${cpf}`)
  cpfsQuery[0] = cpfsQuery[0].replace("&cpf=", "")
  const response = await Service.$http.get(`
        ${process.env.VUE_APP_USERS_API}/usuarios/usuariosByCpfs?cpf=${cpfsQuery.join("")}
    `)
  if (response && response.status === 200)
    return response.data.map((data: any) => new UsuarioSumario(data))
  throw new PortalError(response.data?.msg ?? response.data?.message ?? "Falha ao buscar usuários por CPF")
}

export default usuarioService;
